import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid, Typography } from "@mui/material"
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useParams } from 'react-router-dom';

import MainContainer from "../../components/Container/MainContainer"
import BlockContainer from "../../components/Container/BlockContainer"
import AppContentHeader from '../../components/AppContentHeader';
import BaseLink from '../../components/BaseLink';

const NutritionFoodCategory = () => {
  const { food } = useParams()
  const reports = useSelector(state => state.patientReports)
  const nutritionData = useMemo(() => reports.find(item => item.name === 'Nutrition'), [reports])
  const functionalFoods = useMemo(() => nutritionData && Object.entries(nutritionData?.data?.['Functional Foods']), [nutritionData])

  const supplements = useMemo(() => {
    if (nutritionData?.data?.Supplements) {
      return [...nutritionData?.data?.Supplements].map(item => ({
        substance: item?.Substance,
        dose: item.dose
      }))
    }
    return []
  }, [nutritionData])
  
  const selectedFood = useMemo(() => {
    if (functionalFoods && functionalFoods.length > 0) {
      return functionalFoods.find(item => item[0] === food)
    }
    return null
  }, [functionalFoods, food])

  const selectedFoodSupplements = useMemo(() => {
    if (selectedFood && selectedFood.length > 0) {
      return Object.entries(selectedFood[1]).filter(item => item[0] !== 'Composite' && item[0] !== 'category' && item[0] !== 'example foods')
    }
    return null
  }, [selectedFood])

  return (
    <MainContainer
      sx={{
        padding: 0,
        borderRadius: 0,
        overflowX: 'hidden',
        background: "linear-gradient(180deg, #F6F9FC 0%, #C6E7FF 100%)",
      }}
    >
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Box className='content-header-wave'></Box>
        <BlockContainer
          sx={{
            position: 'relative',
            padding: '16px 20px',
            zIndex: 10,
            mt: '-90px'
          }}
        >
          <AppContentHeader backLabel='Nutrition' pdfDownloadUrl={nutritionData.pdfUrl}/>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
              padding: '50px 0 38px',
              position: 'relative'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mt: '50px'
              }}
            >
              <Typography variant="h1">
                {food}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              borderRadius: '8px',
              padding: '16px',
              bgcolor: 'white',
              boxShadow: '0px 2px 10px 0px #5B87EF33'
            }}
          >
            <Grid container spacing='16px'>
              <Grid item xs={12} mmd={6}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Typography variant="h3">
                    Absorption score
                  </Typography>
                  <HelpOutlineIcon 
                    sx={{
                      color: 'primary.darkGray',
                      fontSize: '20px',
                      ml: '4px'
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant='body' color='primary.darkGray'>
                    Score measured from the number of variants you have that negatively affect your absorption of this nutrient.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: 'primary.lightGreen1',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: '24px'
                  }}
                >
                  <Typography
                    variant='h1'
                    sx={{
                      fontSize: '40px'
                    }}
                  >
                    {selectedFood?.[1]?.Composite}
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: 'primary.darkGreen',
                      borderRadius: '4px',
                      padding: '2px 16px'
                    }}
                  >
                    <Typography variant='body' color='white'>{selectedFood?.[1]?.category}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} mmd={6}>
                <Box>
                  <Typography variant='subText'>Supplements that can cover</Typography>
                  <Grid container spacing={'8px'} mt='1px'>
                    {selectedFoodSupplements && selectedFoodSupplements.map(((item, idx) => (
                      <Grid item xs={12} key={`${item[0]}-${idx}`}>
                        <BaseLink to={`/nutrition-supplement/${item[0]}`}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              width: '100%',
                              borderRadius: '8px',
                              bgcolor: 'primary.black',
                            }}
                          >
                            <Box
                              sx={{
                                padding: '8px 16px',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <Typography variant="body" color="white">
                                {`${item[0]}`}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                bgcolor: 'primary.darkGray',
                                borderRadius: '0 8px 8px 0',
                                padding: '8px 16px',
                                minWidth: '90px',
                                textAlign: 'right'
                              }}
                            >
                              <Typography variant="body" color="white">{supplements.find(sup => sup.substance === item[0])?.dose}</Typography>
                            </Box>
                          </Box>
                        </BaseLink>
                      </Grid>
                    )))}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              mt: '16px',
              borderRadius: '8px',
              padding: '16px',
              bgcolor: 'white',
              boxShadow: '0px 2px 10px 0px #5B87EF33'
            }}
          >
            <Box>
              <Typography variant="h4" color='primary.darkGray'>Example food sources</Typography>
              {/* <BaseLink to={`/nutrition-single-foods/${exam}`} key={exam}> */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  mt: '8px'
                }}
              >
                {selectedFood && selectedFood?.[1]?.['example foods'].map(((exam) => (
                  <Box
                    key={exam}
                    sx={{
                      // bgcolor: 'white',
                      // borderRadius: '4px',
                      // borderLeft: '4px solid',
                      // borderColor: idx % 2 === 0 ? 'primary.darkGreen' : 'primary.mediumGray',
                      // boxShadow: '0px 2px 10px 0px #5B87EF33',
                      // padding: '16px',
                      // mt: '16px'
                      padding: '16px',
                      border: '1px solid',
                      borderColor: 'primary.lightGray',
                      borderRadius: '4px',
                      mr: '8px',
                      mt: '8px'
                    }}
                  >
                    <Typography variant="body" color='primary.darkGray'>
                      {exam}
                    </Typography>
                  </Box>
                )))}
              </Box>
            </Box>
          </Box>
        </BlockContainer>
      </Box>
    </MainContainer>
  )
}

export default NutritionFoodCategory